/// <amd-module name="Wit/Common/AgileControls/BacklogLevelSelector" />
import * as React from "react";
import { IObservableArray, IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IIconProps } from "VSSUI/Icon";
import { IMenuItem } from "VSSUI/Menu";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { BacklogLevelConfiguration } from "Wit/Clients/Work/Contracts/Work";
import { IBacklogLevelConfiguration } from "Wit/Common/AgileControls/AgileContracts";
import * as Resources from "Wit/Common/AgileControls/Resources";
import { formatColor } from "Wit/Common/Utilities/FormatColor";

export interface IBacklogLevelSelectorViewArgs {
    /**
     * Localized artifact name
     */
    artifactName?: string;
    // BacklogLevelConfiguration and IBacklogLevelConfiguration are subtly different and it would be good to unify them.
    backlogLevels: IObservableArray<BacklogLevelConfiguration | IBacklogLevelConfiguration>;
    currentBacklogLevelName: IObservableValue<string>;
    iconProps?: IIconProps;
    onBacklogLevelsUpdated?: () => void;
}

export function getBacklogLevelSelector({
    artifactName,
    backlogLevels,
    currentBacklogLevelName,
    iconProps,
    onBacklogLevelsUpdated
}: IBacklogLevelSelectorViewArgs): {
    levelSelector: IObservableValue<IHeaderCommandBarItem>;
    dispose: () => void;
} {
    const defaultLevelSelector = {
        hideDropdownIcon: false,
        className: "backlog-level-selector",
        id: "backlog-level-selector",
        important: true,
        subtle: true
    };

    const levelSelector = new ObservableValue<IHeaderCommandBarItem>({
        ...defaultLevelSelector,
        subMenuProps: {
            id: "backlog-level-selector-menu",
            items: [
                {
                    id: "backlog-level-selector-loading-menuitem",
                    renderMenuItem: () => <Spinner className="flex-self-center margin-16" size={SpinnerSize.small} label={Resources.Loading} />
                }
            ]
        },
        text: Resources.Loading
    });

    // method to convert BackogLevelConfiguration to a menu item
    const convertBacklogItemToMenuItem = (backlogItem: BacklogLevelConfiguration | IBacklogLevelConfiguration): IMenuItem => {
        return {
            ariaLabel: `${backlogItem.name} ${artifactName}`,
            iconProps: iconProps ? { ...iconProps, style: { color: `${formatColor(backlogItem.color)}` } } : undefined,
            id: `backlog-level-${backlogItem.name}`,
            onActivate: () => {
                if (localeIgnoreCaseComparer(backlogItem.name, currentBacklogLevelName.value) !== 0) {
                    currentBacklogLevelName.value = backlogItem.name;

                    levelSelector.value.iconProps = iconProps ? { ...iconProps, style: { color: `${formatColor(backlogItem.color)}` } } : undefined;
                    levelSelector.value.text = backlogItem.name;
                }
            },
            text: backlogItem.name
        } as IMenuItem; // required because IMenuItem doesn't have an ariaLabel property, but it will get rendered
    };

    // subscribe to selected level changes
    const updateSelectedBacklogLevel = (newLevelName: string, levelSelector: IHeaderCommandBarItem) => {
        if (levelSelector.text !== newLevelName) {
            levelSelector.text = newLevelName;

            const backlogLevel = backlogLevels.value.find(level => level.name == newLevelName);

            if (backlogLevel) {
                levelSelector.iconProps = iconProps ? { ...iconProps, style: { color: `${formatColor(backlogLevel.color)}` } } : undefined;
            } else {
                levelSelector.iconProps = undefined;
            }
        }
    };

    const currentBacklogLevelNameSubscription = currentBacklogLevelName.subscribe(newName =>
        updateSelectedBacklogLevel(newName, levelSelector.value)
    );

    // subscribe to visible level changes, updating menu items as necessary
    const backlogLevelsSubscription = backlogLevels.subscribe(() => {
        const newSelector = {
            ...defaultLevelSelector,
            subMenuProps: {
                id: "backlog-level-selector-menu",
                items: backlogLevels.value.map(convertBacklogItemToMenuItem)
            }
        };

        if (backlogLevels.value.find(level => level.name === currentBacklogLevelName.value) === undefined && backlogLevels.length) {
            currentBacklogLevelName.value = backlogLevels.value[0].name;
        }

        updateSelectedBacklogLevel(currentBacklogLevelName.value, newSelector);
        levelSelector.value = newSelector;

        onBacklogLevelsUpdated?.();
    });

    return {
        levelSelector: levelSelector,
        dispose: () => {
            backlogLevels.unsubscribe(backlogLevelsSubscription);
            currentBacklogLevelName.unsubscribe(currentBacklogLevelNameSubscription);
        }
    };
}
